import React, {useMemo, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {Form, Formik} from "formik";
// local
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import Divider from "components/Divider";
import Select from "components/selects/Select";
import {Modal} from "components/Modal";
import FormikField from "components/FormikField";
import {FieldInput} from "components/Field";
import {SignContractMemberSelect} from "./atoms/SignContractMemberSelect";
import {SignContractMemberEmailInput} from "./atoms/SignContractMemberEmailInput";
import {SignContractCompanyInfoTag} from "./atoms/SignContractCompanyInfoTag";
import {IconSearch, IconChevronDown} from "components/icons";
import {signContractMemberSchema, initialManuallyMember} from "../constants";
import {getSelectListMembers, getFullName} from "helpers/helper";
import {color} from "constants/color.consts";

export const SignContractAddMemberModal = ({
  onSearchCompany,
  onSeacrhEmail,
  onSearchMembers,
  onAccept,
  onClose,
}) => {
  const {t} = useTranslation();
  const formRef = useRef();

  const [company, setCompany] = useState(null);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [companyMemberId, setCompanyMemberId] = useState(null);
  const [email, setEmail] = useState(null);
  const [emailCompanyMembers, setEmailCompanyMembers] = useState([]);
  const [stepDirection, setStepDirection] = useState(null);
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(false);

  const isActionButtons = useMemo(
    () => stepDirection === "company" && step === 0 && companyMemberId,
    [stepDirection, step, companyMemberId]
  );
  const searchCompanies = async (value) => await onSearchCompany(value);

  const addEmailMember = (value) => {
    if (email === value) return;
    setEmail(value);
    setLoading(true);
    onSeacrhEmail(value)
      .then((data) => {
        setStepDirection("email");
        setEmailCompanyMembers(data);
        setStep(data.length ? 0 : 1);
      })
      .finally(() => setLoading(false));
  };
  const ActionButtons = ({disabled, onClick}) => (
    <div className="df mt-8">
      <ButtonTransparent
        name="CANCEL"
        medium
        onClick={onClose}
        color="red"
        loading={loading}
        addClass="mla"
      />
      <ButtonTransparent
        name="SAVE"
        type="submit"
        onClick={onClick}
        loading={loading}
        disabled={disabled}
        addClass="ml-4"
        medium
        nonTransparent
      />
    </div>
  );

  const CompanySelect = () => (
    <div className="df-row-center">
      <IconSearch height="30" width="30" color={color.green} addClass="mr-2" />
      <SignContractMemberSelect
        search={searchCompanies}
        add={(org) => {
          setCompany(org);
          setStepDirection("company");
          setStep(0);
          onSearchMembers(org.id).then((members) =>
            setCompanyMembers(getSelectListMembers(members, "id"))
          );
        }}
      />
    </div>
  );

  const InitialState = () => (
    <React.Fragment>
      <div className="__bold __uppercase fz-12 mt-4 mb-2">
        {t("SIGN_CONTRACT_MEMBER_ADD_MODAL_INITIAL_COMPANY_LABEL")}
      </div>
      <CompanySelect />
      <Divider addClass="mt-4 mb-4" solid />
      <div className="__bold __uppercase fz-12 mb-2">
        {t("SIGN_CONTRACT_MEMBER_ADD_MODAL_INITIAL_EMAIL_LABEL")}
      </div>
      <SignContractMemberEmailInput
        defaultValue={email}
        loading={loading}
        edit
        onAdd={addEmailMember}
      />
    </React.Fragment>
  );

  const FirstEmailStep = () => (
    <React.Fragment>
      <div className="__bold __uppercase fz-12 mt-4">
        {t("SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP01_COMPANY_LABEL")}
      </div>
      {emailCompanyMembers?.map((member) => (
        <div key={member.id} className="df-row-jsb mt-4">
          <div className="df-row-center">
            <SignContractCompanyInfoTag
              number={member?.Organization?.Company?.companyNumber}
              name={member?.Organization?.Company?.companyName}
              numberAsTooltip
              bordered
            />
            <span className="ml-2 mr-2">
              {getFullName(member.User?.firstName, member.User?.lastName)}
            </span>
            <span>{member.User?.email}</span>
          </div>
          <ButtonTransparent
            loading={loading}
            name="SIGN_CONTRACT_MEMBER_ADD_MODAL_ADD_BUTTON"
            small
            addClass="ml-2"
            onClick={() => {
              setLoading(true);
              onAccept({
                OrganizationMemberId: member.id,
                isInitiatingSide: false,
              }).finally(() => setLoading(false));
            }}
          />
        </div>
      ))}
      <Divider addClass="mt-4 mb-4" solid />
      <div className="__bold __uppercase fz-12 mb-2">
        {t("SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP01_EMAIL_LABEL")}
      </div>
      <SignContractMemberEmailInput
        defaultValue={email}
        loading={loading}
        edit
        onAdd={addEmailMember}
      />
    </React.Fragment>
  );
  const SecondEmailStep = () => (
    <React.Fragment>
      <SignContractMemberEmailInput defaultValue={email} addClass="mt-4" />
      <Formik
        enableReinitialize
        initialValues={initialManuallyMember}
        validationSchema={signContractMemberSchema}
        onSubmit={onManuallyMemberSubmit}
        innerRef={formRef}
      >
        {({dirty, isValid, values, setFieldValue}) => (
          <Form className="manually-member-form">
            <div className="df-row-start">
              <FormikField
                name="firstName"
                placeholder={t(
                  "SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_FIRSTNAME_PLACEHOLDER"
                )}
                label={t("SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_FIRSTNAME_LABEL")}
                small
                fullWidth
                highlightIfEmpty={!values.firstName}
                errorName="firstName"
                className="input"
                addClass="mr-4 mb-4"
              />

              <FormikField
                name="lastName"
                placeholder={t(
                  "SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_LASTNAME_PLACEHOLDER"
                )}
                label={t("SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_LASTNAME_LABEL")}
                small
                fullWidth
                highlightIfEmpty={!values.lastName}
                errorName="lastName"
                className="input"
              />
            </div>

            <div className="df-row-start">
              <FieldInput
                name="companyNumber"
                placeholder="SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_COMPANY_NUMBER_PLACEHOLDER"
                label="SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_COMPANY_NUMBER_LABEL"
                small
                errorName="companyNumber"
                addWrapperClass="mr-4"
                className="wdth-175"
                onChange={(e) => setFieldValue("companyNumber", e.target.value)}
              />
              <FormikField
                name="companyName"
                placeholder={t(
                  "SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_COMPANY_NAME_PLACEHOLDER"
                )}
                label={t("SIGN_CONTRACT_MEMBER_ADD_MODAL_STEP02_COMPANY_NAME_LABEL")}
                small
                fullWidth
                errorName="companyName"
                className="input"
              />
            </div>
            {dirty && isValid && <ActionButtons />}
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );

  const FirstCompanyStep = () => (
    <React.Fragment>
      <div className="df-row-jsb mt-4 mb-4">
        <span>{company.name}</span>
        <SignContractCompanyInfoTag
          number={company?.Company.companyNumber}
          name={company?.Company.companyName}
          bordered
        />
      </div>
      <Select
        title="SIGN_CONTRACT_MEMBER_ADD_MODAL_COMPANY_MEMBER_LABEL"
        blackTitle
        smallTitle
        needSearch
        showArrow
        shadow
        large
        fullWidth
        placeholder="SIGN_CONTRACT_MEMBER_ADD_MODAL_COMPANY_MEMBER_PLACEHOLDER"
        searchPlaceholder={t(
          "SIGN_CONTRACT_MEMBER_ADD_MODAL_COMPANY_MEMBER_SEARCH_PLACEHOLDER"
        )}
        notFoundContent="SIGN_CONTRACT_MEMBER_ADD_MODAL_COMPANY_MEMBER_NO_DATA"
        list={companyMembers}
        value={companyMemberId}
        onChange={setCompanyMemberId}
        addDropdownClass="recipients-tab_custom-select-dropdown"
        suffixIcon={<IconChevronDown />}
      />
    </React.Fragment>
  );

  const steps = {
    email: [<FirstEmailStep />, <SecondEmailStep />],
    company: [<FirstCompanyStep />],
  };

  const onManuallyMemberSubmit = (values) =>
    handleAccept({
      ...values,
      companyNumber: values.companyNumber?.replace("-", ""),
      email,
    });

  const handleAccept = (values) => {
    setLoading(true);
    onAccept(values).finally(() => setLoading(false));
  };

  return (
    <Modal
      titleIcon={<React.Fragment />}
      titleText="SIGN_CONTRACT_SIGN_MODAL_ADD_MEMBER_TITLE"
      tightTitle
      boldTitle
      uppercaseTitle
      fontSizeTitle="12"
      noCenterContent
      width="800"
      rounded
      onClose={onClose}
    >
      <div className="pb-8 pl-8 pr-8">
        {stepDirection ? steps[stepDirection][step] : <InitialState />}
        {isActionButtons && (
          <ActionButtons
            onClick={() =>
              handleAccept({
                OrganizationMemberId: companyMemberId,
                isInitiatingSide: false,
              })
            }
          />
        )}
      </div>
    </Modal>
  );
};
